import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { readablePairs } from '@/lib/audit-text';
import type { ColumnSpec } from './record-table';
import { FieldList } from './record-table';

/**
 * A row's long detail, kept out of the table: a button that opens the whole
 * record in a dialog, its named fields first, then the detail as plain pairs.
 */
export function DetailsButton({
  title,
  fields,
  record,
  detail,
}: Readonly<{
  title: string;
  fields: readonly ColumnSpec[];
  record: Record<string, unknown>;
  detail: unknown;
}>) {
  const [open, setOpen] = useState(false);
  const pairs = readablePairs(detail);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        View details
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              The whole record, with its full detail.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-5">
            {fields.length > 0 ? (
              <FieldList fields={fields} record={record} />
            ) : null}
            {pairs.length > 0 ? (
              <section className="flex flex-col gap-2">
                <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Detail
                </h3>
                <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-6 gap-y-2 text-sm">
                  {pairs.map((pair) => (
                    <div key={pair.label} className="contents">
                      <dt className="text-muted-foreground">{pair.label}</dt>
                      <dd className="min-w-0 break-words">{pair.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
